import { ArrowDownIcon } from '@heroicons/react/16/solid'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import DataTable from 'react-data-table-component'

import useFillViewport from '@/hooks/FillViewport'
import baseColors from '@/utils/colors'
import { FOOTER_HEIGHT } from '@/utils/constants'

// What sits below a filled table, so the page itself never scrolls (2026-10-01): the
// table's pagination, measured, then AppShell's bottom padding (py-6) and the footer.
const PAGE_BOTTOM_PADDING = 24
const spaceBelowTable = (element) =>
  (element.querySelector('.rdt_Pagination')?.offsetHeight || 0) +
  PAGE_BOTTOM_PADDING +
  FOOTER_HEIGHT

/**
 *
 * DataTable
 *
 * - Displays a table of data
 * - With `fillViewport`, the table's body is as tall as the screen allows and scrolls
 *   inside itself, so its header and pagination stay in view. `fillViewportWatch` lists
 *   values that move the table on the page (a collapsing panel above it, say).
 */
const CustomDataTable = ({
  columns,
  data,
  fillViewport,
  fillViewportWatch,
  onChangePage,
  pagination,
  paginationServer,
  progressPending,
  responsive,
  title,
  highlightOnHover,
  ...rest
}) => {
  const [fillRef, fillHeight] = useFillViewport({
    reserve: spaceBelowTable,
    watch: fillViewportWatch,
  })

  // New rows (the next page, a new sort) start at the top. The table scrolls inside
  // itself, so otherwise the next page opens wherever the last one was left.
  useEffect(() => {
    if (!fillViewport) return
    // The scrolling box is the table's nearest ancestor that scrolls, inside our wrapper.
    let scroller = fillRef.current?.querySelector('.rdt_Table')
    while (scroller && scroller !== fillRef.current) {
      if (['auto', 'scroll'].includes(getComputedStyle(scroller).overflowY)) break
      scroller = scroller.parentElement
    }
    if (scroller && scroller !== fillRef.current) scroller.scrollTop = 0
  }, [data, fillViewport])

  const table = (
    <DataTable
      columns={columns}
      highlightOnHover={highlightOnHover}
      customStyles={{
        responsiveWrapper: {
          style: {
            border: `1px solid #cfd0d2`,
            borderRadius: '16px',
          },
        },
        columnHeader: {
          style: {
            backgroundColor: '#FFFFFF',
            borderBottomColor: `${baseColors.brownGray.DEFAULT}`,
          },
        },
        headRow: {
          style: {
            backgroundColor: `${baseColors.brownGray[50]}`,
            borderBottom: 'none',
            color: `${baseColors.charcoal.DEFAULT}`,
            fontSize: '10px',
            fontWeight: '900',
            height: '40px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          },
        },
        rows: {
          style: {
            fontSize: '15px',
            lineHeight: '20px',
            color: `${baseColors.charcoal[900]}`,
            minHeight: '52px',
            '&:not(:last-of-type)': {
              borderBottomColor: `${baseColors.brownGray.DEFAULT}`,
            },
            '&:last-of-type': {
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
            },
            cursor: highlightOnHover ? 'pointer' : 'default',
          },
        },
        pagination: {
          style: {
            color: '#3b3b3e',
            fontSize: '12px',
            minHeight: '30px',
            backgroundColor: 'transparent',
            border: 'none',
            fontWeight: '600',
          },
          pageButtonsStyle: {
            borderRadius: '50%',
            height: '32px',
            width: '32px',
            padding: '4px',
            margin: '5px',
            cursor: 'pointer',
            transition: '0.4s',
            outline: 'none',
            color: 'black',
            fill: 'black',
            backgroundColor: 'transparent',
            '&:disabled': {
              cursor: 'unset',
              color: 'black',
              opacity: 0.25,
              fill: 'black',
            },
            '&:hover:not(:disabled)': {
              backgroundColor: '#bee2f9',
              fill: 'white',
            },
            '&:focus': {
              outline: 'none',
              backgroundColor: '#bee2f9',
              fill: 'white',
            },
          },
        },
      }}
      data={data}
      fixedHeader
      // Needs a CSS length. The bare `200` is not one, so it caps nothing and other tables
      // keep running full length, as they always have; undefined would cap them at 100vh.
      fixedHeaderScrollHeight={fillViewport ? `${fillHeight}px` : 200}
      onChangePage={onChangePage}
      pagination={pagination}
      paginationServer={paginationServer}
      progressPending={progressPending}
      title={title}
      responsive
      sortIcon={<ArrowDownIcon style={{ height: '12px' }} />}
      {...rest}
    />
  )

  return fillViewport ? <div ref={fillRef}>{table}</div> : table
}

CustomDataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  fillViewport: PropTypes.bool,
  fillViewportWatch: PropTypes.array,
  onChangePage: PropTypes.func,
  pagination: PropTypes.bool,
  paginationServer: PropTypes.bool,
  progressPending: PropTypes.bool.isRequired,
  responsive: PropTypes.bool,
  title: PropTypes.string,
  highlightOnHover: PropTypes.bool,
}

CustomDataTable.defaultProps = {
  fillViewport: false,
  fillViewportWatch: [],
  onChangePage: null,
  pagination: false,
  paginationServer: false,
  responsive: false,
  title: null,
  highlightOnHover: false,
}

export default CustomDataTable
